import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Phone, Calendar, ChevronRight } from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import SeoHead from "../components/SeoHead";
import { CATEGORIAS, servicosPorCategoria } from "../data/servicos";

const Badge = ({ children }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#88A096]/15 text-[#1E5F6E] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider">
    {children}
  </span>
);

const ServiceCard = ({ s }) => {
  const Icon = s.icon;
  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.45 }}
      data-testid={`service-card-${s.slug}`}
      className="group rounded-2xl bg-white border border-[#1E5F6E]/8 p-6 hover:border-[#1E5F6E]/25 hover:-translate-y-1 hover:shadow-soft transition-all"
    >
      <div className="flex items-start gap-4">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#1E5F6E]/8 text-[#1E5F6E] group-hover:bg-[#1E5F6E] group-hover:text-white transition-colors">
          <Icon className="h-5 w-5" strokeWidth={2.1} />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-lg md:text-xl font-bold text-[#1A1A1A] leading-snug">
            {s.nome}
          </h3>
          <p className="mt-2 text-[14.5px] text-[#5A5A5A] leading-relaxed">
            {s.descricao}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {s.duracao && (
              <span className="text-xs font-semibold text-[#5A5A5A]">
                Duração: <span className="text-[#1A1A1A]">{s.duracao} min</span>
              </span>
            )}
            {s.online && <Badge>Marcação online disponível</Badge>}
          </div>
        </div>
      </div>
    </motion.article>
  );
};

export default function ServicosPage() {
  const [activeCat, setActiveCat] = useState(CATEGORIAS[0].slug);

  useEffect(() => {
    const sections = CATEGORIAS.map((c) => document.getElementById(c.slug)).filter(
      Boolean
    );
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActiveCat(visible.target.id);
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: [0, 0.1, 0.3, 0.6, 1] }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const scrollTo = (slug) => {
    const el = document.getElementById(slug);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 100;
    window.scrollTo({ top, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#1A1A1A]" data-testid="servicos-page">
      <SeoHead
        title="Serviços | Vet Clinic — Clínica Veterinária na Madeira"
        description="Cuidados veterinários completos na Madeira: consultas, vacinação, cirurgia, urgências e PetShop. Marque online em Ribeira Brava ou Calheta."
      />
      <Header />
      <main>
        {/* Hero curto */}
        <section className="relative overflow-hidden" data-testid="servicos-hero">
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(900px 400px at 80% -10%, rgba(30,95,110,0.10), transparent 60%), linear-gradient(180deg, #FAF7F2 0%, #EFE9DF 100%)",
            }}
          />
          <div className="absolute inset-0 grain opacity-60 pointer-events-none" />
          <div className="container-app relative pt-16 pb-12 md:pt-24 md:pb-16">
            <div className="max-w-3xl">
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#88A096] mb-3">
                Serviços
              </span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-[60px] font-extrabold text-[#1A1A1A] leading-[1.05] tracking-tight">
                Os nossos <span className="text-[#1E5F6E]">serviços</span>.
              </h1>
              <p className="mt-5 text-lg md:text-xl text-[#5A5A5A] max-w-2xl leading-relaxed">
                Tudo o que o seu animal precisa, num só lugar.
              </p>
            </div>
          </div>
        </section>

        {/* Mobile tabs */}
        <div className="lg:hidden sticky top-16 z-40 bg-[#FAF7F2]/95 backdrop-blur-md border-y border-[#1E5F6E]/10">
          <div
            className="container-app flex gap-2 overflow-x-auto hide-scrollbar py-3"
            data-testid="servicos-tabs-mobile"
          >
            {CATEGORIAS.map((c) => (
              <button
                key={c.slug}
                onClick={() => scrollTo(c.slug)}
                data-testid={`tab-mobile-${c.slug}`}
                className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                  activeCat === c.slug
                    ? "bg-[#1E5F6E] text-white"
                    : "bg-white text-[#1A1A1A] border border-[#1E5F6E]/10"
                }`}
              >
                {c.nome}
              </button>
            ))}
          </div>
        </div>

        {/* Content with sticky sidebar (desktop) */}
        <section className="container-app py-10 lg:py-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          <aside
            className="hidden lg:block lg:col-span-3"
            data-testid="servicos-sidebar"
          >
            <nav className="sticky top-28">
              <div className="text-xs font-bold uppercase tracking-widest text-[#88A096] mb-4">
                Categorias
              </div>
              <ul className="space-y-1">
                {CATEGORIAS.map((c) => {
                  const active = activeCat === c.slug;
                  return (
                    <li key={c.slug}>
                      <button
                        onClick={() => scrollTo(c.slug)}
                        data-testid={`sidebar-${c.slug}`}
                        className={`group flex w-full items-center justify-between gap-2 rounded-xl px-3.5 py-3 text-left text-[15px] transition-colors ${
                          active
                            ? "bg-[#1E5F6E] text-white font-semibold"
                            : "text-[#1A1A1A] hover:bg-white"
                        }`}
                      >
                        <span>{c.nome}</span>
                        <ChevronRight
                          className={`h-4 w-4 transition-transform ${
                            active ? "opacity-100" : "opacity-30 group-hover:opacity-70"
                          }`}
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </aside>

          <div className="lg:col-span-9 space-y-16 md:space-y-20">
            {CATEGORIAS.map((c) => {
              const items = servicosPorCategoria(c.slug);
              return (
                <section
                  key={c.slug}
                  id={c.slug}
                  data-testid={`section-${c.slug}`}
                  className="scroll-mt-28"
                >
                  <div className="mb-7">
                    <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#88A096] mb-2">
                      {c.titulo}
                    </span>
                    <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-[#1A1A1A] tracking-tight">
                      {c.nome}
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {items.map((s) => (
                      <ServiceCard key={s.slug} s={s} />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        </section>

        {/* CTA final — telefones */}
        <section className="container-app pb-20" data-testid="servicos-cta">
          <div className="relative overflow-hidden rounded-[32px] bg-white border border-[#1E5F6E]/10 p-8 md:p-14 shadow-soft">
            <div className="absolute -right-16 -top-16 h-72 w-72 rounded-full bg-[#88A096]/10" aria-hidden />
            <div className="absolute -left-12 -bottom-16 h-60 w-60 rounded-full bg-[#E8A87C]/15" aria-hidden />
            <div className="relative max-w-2xl">
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#88A096] mb-3">
                Estamos cá para si
              </span>
              <h2 className="font-display text-3xl md:text-4xl lg:text-[44px] font-extrabold text-[#1A1A1A] tracking-tight leading-tight">
                Não encontra o que <span className="text-[#1E5F6E]">procura?</span>
              </h2>
              <p className="mt-4 text-base md:text-lg text-[#5A5A5A]">
                Ligue-nos. Estamos cá para responder a todas as suas dúvidas.
              </p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <a
                  href="tel:+351291616284"
                  data-testid="cta-call-ribeira"
                  className="group flex items-center gap-4 rounded-2xl bg-[#1E5F6E] text-white px-5 py-5 hover:bg-[#174E5B] transition-colors"
                >
                  <span className="grid h-12 w-12 place-items-center rounded-xl bg-white/15">
                    <Phone className="h-5 w-5" />
                  </span>
                  <div className="text-left">
                    <div className="text-xs uppercase tracking-widest text-white/70">
                      Ribeira Brava
                    </div>
                    <div className="font-display text-2xl font-extrabold">
                      291 616 284
                    </div>
                  </div>
                </a>
                <a
                  href="tel:+351291827677"
                  data-testid="cta-call-calheta"
                  className="group flex items-center gap-4 rounded-2xl bg-[#1E5F6E] text-white px-5 py-5 hover:bg-[#174E5B] transition-colors"
                >
                  <span className="grid h-12 w-12 place-items-center rounded-xl bg-white/15">
                    <Phone className="h-5 w-5" />
                  </span>
                  <div className="text-left">
                    <div className="text-xs uppercase tracking-widest text-white/70">
                      Calheta
                    </div>
                    <div className="font-display text-2xl font-extrabold">
                      291 827 677
                    </div>
                  </div>
                </a>
              </div>

              <div className="mt-6 text-sm text-[#5A5A5A]">
                Ou{" "}
                <a
                  href="/marcar-consulta"
                  className="inline-flex items-center gap-1 font-bold text-[#1E5F6E] hover:underline underline-offset-4"
                >
                  marque online agora
                  <Calendar className="h-4 w-4" />
                </a>
                .
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
